import { useEffect, useRef, useState } from "react";
import { CheckCircle2, XCircle, RefreshCw, Volume2, Trophy, Sparkles, Zap } from "lucide-react";
import { generateSentence, PATTERNS } from "../lib/sentenceGenerator";
import { earnXP } from "../gamification";
import { playCorrect, playWrong, playLevelUp } from "../sounds";

// Color per word role — same scheme as Lesson breakdown
const ROLE_COLORS = {
  Subject: "from-sky to-ink-900",
  Verb: "from-sage to-sage-dark",
  Object: "from-gold-500 to-gold-600",
  Article: "from-gold-500 to-gold-600",
};
const ROLE_BORDER = {
  Subject: "border-sky-light",
  Verb: "border-sage",
  Object: "border-gold-500",
  Article: "border-gold-500",
};

export default function SentenceBuilderGame({ pattern = PATTERNS.SVO, totalRounds = 10, onComplete, kid = true }) {
  const [round, setRound] = useState(1);
  const [target, setTarget] = useState(() => generateSentence(pattern, { kid }));
  const [shuffled, setShuffled] = useState([]);
  const [slots, setSlots] = useState([]);   // user's chosen order
  const [feedback, setFeedback] = useState(null); // "correct" | "wrong" | null
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);
  const [hint, setHint] = useState(false);

  const dragId = useRef(null);

  // Initialize a new round
  useEffect(() => {
    const t = generateSentence(pattern, { kid });
    setTarget(t);
    // shuffle words with stable IDs
    const withIds = t.words.map((w, i) => ({ ...w, id: `${i}-${Math.random()}` }));
    setShuffled([...withIds].sort(() => Math.random() - 0.5));
    setSlots([]);
    setFeedback(null);
    setHint(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round]);

  function speakWord(text) {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "en-US";
      u.rate = 0.85;
      window.speechSynthesis.speak(u);
    }
  }

  function placeWord(wordObj) {
    if (feedback) return;
    setShuffled((prev) => prev.filter((w) => w.id !== wordObj.id));
    setSlots((prev) => [...prev, wordObj]);
    speakWord(wordObj.text);
  }

  function removeFromSlot(wordObj) {
    if (feedback) return;
    setSlots((prev) => prev.filter((w) => w.id !== wordObj.id));
    setShuffled((prev) => [...prev, wordObj]);
  }

  function checkAnswer() {
    if (slots.length !== target.words.length) return;
    const userText = slots.map((s) => s.text).join(" ").toLowerCase();
    const targetText = target.text.toLowerCase();
    if (userText === targetText) {
      setFeedback("correct");
      const newCombo = combo + 1;
      const points = 10 + newCombo * 2;
      setCombo(newCombo);
      setBestCombo((b) => Math.max(b, newCombo));
      setScore((s) => s + points);
      earnXP(points, "game");
      try { playCorrect(); } catch {}
      window.dispatchEvent(new CustomEvent("answer:correct"));
    } else {
      setFeedback("wrong");
      setCombo(0);
      try { playWrong(); } catch {}
      window.dispatchEvent(new CustomEvent("answer:wrong"));
    }
  }

  function nextRound() {
    if (round >= totalRounds) {
      try { playLevelUp(); } catch {}
      onComplete?.({ score, bestCombo, totalRounds });
      return;
    }
    setRound((r) => r + 1);
  }

  function tryAgain() {
    setShuffled([...target.words.map((w, i) => ({ ...w, id: `${i}-${Math.random()}` }))].sort(() => Math.random() - 0.5));
    setSlots([]);
    setFeedback(null);
  }

  function showHint() {
    setHint(true);
    setTimeout(() => setHint(false), 2500);
  }

  // HTML5 drag handlers
  function onDragStart(e, wordObj) {
    dragId.current = wordObj.id;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", wordObj.id); } catch {}
  }
  function onDropOnSlots(e) {
    e.preventDefault();
    const id = dragId.current || e.dataTransfer.getData("text/plain");
    const wordObj = shuffled.find((w) => w.id === id);
    if (wordObj) placeWord(wordObj);
    dragId.current = null;
  }
  function onDropOnBank(e) {
    e.preventDefault();
    const id = dragId.current || e.dataTransfer.getData("text/plain");
    const wordObj = slots.find((w) => w.id === id);
    if (wordObj) removeFromSlot(wordObj);
    dragId.current = null;
  }
  const allowDrop = (e) => e.preventDefault();

  if (round > totalRounds && feedback === null) {
    return (
      <div className="text-center py-8 space-y-4">
        <div className="text-7xl animate-bounce">🏆</div>
        <h2 className="text-3xl font-black text-gray-900 dark:text-white">Round complete!</h2>
        <p className="text-2xl font-black text-gold-700">{score} XP</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">Best combo: 🔥 {bestCombo}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* HUD */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-cream-100 dark:bg-ink-900/40 border border-gold-500 dark:border-gold-500">
          <span className="text-xs font-black text-gold-700 dark:text-paper">Round {round}/{totalRounds}</span>
        </div>
        <div className="flex items-center gap-2">
          {combo >= 2 && (
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-gold-500 to-berry-light0 text-white shadow-lg animate-pulse">
              <Zap className="w-3.5 h-3.5" />
              <span className="text-xs font-black">{combo}× COMBO!</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-gold-500 to-gold-600 text-white shadow-lg">
            <Sparkles className="w-3.5 h-3.5" />
            <span className="text-xs font-black">{score} XP</span>
          </div>
        </div>
      </div>

      {/* Target hint */}
      <div className="text-center">
        <p className="text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1">Build this sentence:</p>
        <button
          onClick={() => speakWord(target.text)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-ink-800 border-2 border-dashed border-gold-500 dark:border-gold-500 hover:border-gold-500 transition group"
        >
          <Volume2 className="w-4 h-4 text-gold-700 group-hover:scale-110 transition" />
          <span className="text-base font-bold text-gray-900 dark:text-white">
            {hint ? `"${target.text}"` : "Listen 🎧"}
          </span>
        </button>
        {!hint && (
          <button onClick={showHint} className="block mx-auto mt-2 text-xs text-gray-400 underline hover:text-gold-700">
            Show me the answer
          </button>
        )}
        {hint && <p className="text-xs text-gold-700 mt-2 italic animate-pulse">Hiding in 2s…</p>}
      </div>

      {/* Slot row (where user assembles the sentence) */}
      <div
        onDrop={onDropOnSlots}
        onDragOver={allowDrop}
        className={`min-h-[100px] p-4 rounded-2xl border-4 border-dashed transition-all flex flex-wrap gap-2 items-center justify-center ${
          feedback === "correct" ? "border-sage bg-sage-light dark:bg-sage-dark/20" :
          feedback === "wrong" ? "border-rust-light bg-rust-light dark:bg-rust-dark/20 animate-shake" :
          "border-gold-500 bg-paper-deep/50 dark:bg-ink-900/10"
        }`}
      >
        {slots.length === 0 && (
          <p className="text-sm text-gray-400 italic">Drop or tap words here ⬇️</p>
        )}
        {slots.map((w) => (
          <button
            key={w.id}
            draggable={!feedback}
            onDragStart={(e) => onDragStart(e, w)}
            onClick={() => removeFromSlot(w)}
            className={`px-4 py-2.5 rounded-xl bg-gradient-to-br ${ROLE_COLORS[w.role] || "from-gray-500 to-gray-600"} text-white font-black text-base shadow-md hover:scale-110 transition-transform cursor-grab active:cursor-grabbing`}
          >
            <span className="mr-1.5">{w.emoji}</span>{w.text}
          </button>
        ))}
      </div>

      {/* Feedback */}
      {feedback === "correct" && (
        <div className="text-center py-3 px-4 bg-gradient-to-r from-sage to-sage-dark text-white rounded-2xl shadow-lg animate-fade-in">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-1" />
          <p className="font-black">Perfect! "{target.text}"</p>
          <button onClick={nextRound} className="mt-3 inline-flex items-center gap-1.5 bg-white text-sage-dark font-black px-5 py-2 rounded-full text-sm hover:scale-105 transition">
            {round < totalRounds ? "Next →" : "Finish 🏆"}
          </button>
        </div>
      )}
      {feedback === "wrong" && (
        <div className="text-center py-3 px-4 bg-gradient-to-r from-rust-light to-berry-light0 text-white rounded-2xl shadow-lg animate-fade-in">
          <XCircle className="w-8 h-8 mx-auto mb-1" />
          <p className="font-bold text-sm">Not quite. Try again!</p>
          <p className="text-xs opacity-80 mt-1">You said: "{slots.map((s) => s.text).join(" ")}"</p>
          <button onClick={tryAgain} className="mt-3 inline-flex items-center gap-1.5 bg-white text-rust-dark font-black px-5 py-2 rounded-full text-sm hover:scale-105 transition">
            <RefreshCw className="w-4 h-4" /> Try again
          </button>
        </div>
      )}

      {/* Word bank */}
      <div
        onDrop={onDropOnBank}
        onDragOver={allowDrop}
        className="p-4 rounded-2xl bg-white dark:bg-ink-800 border-2 border-gray-200 dark:border-gray-700"
      >
        <p className="text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-3 text-center">Words to use ↓</p>
        <div className="flex flex-wrap gap-2 justify-center">
          {shuffled.length === 0 && !feedback && (
            <p className="text-sm text-gray-400 italic">Tap "Check" when ready!</p>
          )}
          {shuffled.map((w) => (
            <button
              key={w.id}
              draggable={!feedback}
              onDragStart={(e) => onDragStart(e, w)}
              onClick={() => placeWord(w)}
              className={`px-4 py-2.5 rounded-xl bg-white dark:bg-gray-700 border-2 ${ROLE_BORDER[w.role] || "border-gray-300"} font-bold text-sm text-gray-800 dark:text-white hover:scale-110 hover:shadow-lg transition-transform cursor-grab active:cursor-grabbing`}
            >
              <span className="mr-1.5">{w.emoji}</span>{w.text}
            </button>
          ))}
        </div>
      </div>

      {/* Check button */}
      {!feedback && shuffled.length === 0 && slots.length > 0 && (
        <button
          onClick={checkAnswer}
          className="w-full bg-gradient-to-r from-ink-900 to-gold-600 text-white font-black py-4 rounded-2xl text-base shadow-xl hover:scale-[1.02] transition flex items-center justify-center gap-2"
        >
          <Trophy className="w-5 h-5" /> CHECK MY SENTENCE
        </button>
      )}
    </div>
  );
}
